<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-bold text-gray-900">
          マジックリンク認証
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          認証中...
        </p>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="flex flex-col items-center space-y-4">
        <div class="flex items-center justify-center">
          <svg class="animate-spin h-12 w-12 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p class="text-sm text-gray-600">認証処理中です。しばらくお待ちください...</p>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="space-y-4">
        <div class="alert-danger">
          {{ errorMessage }}
        </div>
        <div class="text-center">
          <router-link
            to="/auth/login"
            class="text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            ← ログインページに戻る
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '../../stores/auth.js'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const authStore = useAuthStore()

const loading = ref(true)
const errorMessage = ref('')

const verifyMagicLink = async () => {
  const token = route.params.token

  // トークンの基本バリデーション（64文字の英数小文字）
  if (!token || !/^[a-z0-9]{64}$/.test(token)) {
    errorMessage.value = 'マジックリンクのトークンが無効です。'
    loading.value = false
    toast.error(errorMessage.value)

    // 3秒後にログインページにリダイレクト
    setTimeout(() => {
      router.push('/auth/login')
    }, 3000)
    return
  }

  try {
    // マジックリンク認証を実行
    await authStore.verifyMagicLink(token)

    toast.success('ログインしました')

    // リダイレクト先に遷移（デフォルトはダッシュボード）
    const redirectPath = route.query.redirect || '/dashboard'
    router.push(redirectPath)
  } catch (error) {
    console.error('Magic Link Verify error:', error)
    errorMessage.value = error.message || 'マジックリンク認証に失敗しました。リンクが無効または期限切れの可能性があります。'
    toast.error(errorMessage.value)

    // 3秒後にログインページにリダイレクト
    setTimeout(() => {
      router.push('/auth/login')
    }, 3000)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // 既にログイン済みの場合はダッシュボードにリダイレクト
  if (authStore.isAuthenticated) {
    toast.info('既にログイン済みです')
    router.push('/dashboard')
    return
  }

  // マジックリンク認証を実行
  verifyMagicLink()
})
</script>
